import React from 'react';
import { Table } from 'react-bootstrap';
import { Product } from '../../../../api/shop/types/productTypes';
import { StatusBadge } from '../../../../Common/BadgeStyles';
import '../../../../Common/BadgeStyles.css'; // Ensure badge styles are loaded
import '../../../../Common/ManageTournamentsFixturesTable.css'; // Table styling
import './ShopServicesTable.css'; // Shop Services specific table overrides
import ShimmerLoader from '../../../../Common/ShimmerLoader';

interface ProductsTableProps {
  products: Product[];
  loading: boolean;
  pagination: {
    page: number;
    totalPages: number;
    totalCount: number;
  };
  onPageChange: (page: number) => void;
  onViewProduct?: (productId: string) => void;
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  loading,
  pagination,
  onPageChange,
  onViewProduct,
}) => {
  const formatPrice = (price: number) => {
    return `₹${price.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return <ShimmerLoader type="table" rows={5} />;
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No products found</p>
      </div>
    );
  }

  return (
    <div className="manage-tournaments-table-container">
      <Table responsive hover className="mb-0 manage-tournaments-table shop-services-table">
        <thead>
          <tr>
            <th>Product Name</th>
            <th>Quantity</th>
            <th>Product ID</th>
            <th>Price</th>
            <th>Status</th>
            <th>Product Link</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product._id}>
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{product.name}</td>
              <td>{product.quantity}</td>
              <td>{product.productId}</td>
              <td>{formatPrice(product.price)}</td>
              <td>
                <StatusBadge status={product.status === 'ACTIVE' ? 'ACTIVE' : 'INACTIVE'} />
              </td>
              <td>
                {product.productLink ? (
                  <a
                    href={product.productLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#006EFF', textDecoration: 'none' }}
                  >
                    View Product
                  </a>
                ) : product.productId || product._id ? (
                  <a
                    href={`/shop-services/products/${product.productId || product._id}`}
                    style={{ color: '#006EFF', textDecoration: 'none' }}
                  >
                    View Product
                  </a>
                ) : onViewProduct ? (
                  <button
                    className="btn btn-link p-0"
                    onClick={() => onViewProduct(product._id)}
                    style={{ color: '#006EFF', textDecoration: 'none' }}
                  >
                    View Product
                  </button>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="text-muted">
            Showing {((pagination.page - 1) * 10) + 1} - {Math.min(pagination.page * 10, pagination.totalCount)} of {pagination.totalCount} products
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              Previous
            </button>
            <span className="d-flex align-items-center px-3">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsTable;
